import { Component, Input, OnInit } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { TaxonomyOverridesService } from 'src/app/services/taxonomy-overrides.service';
import { LoggerService } from '../../../../services/logger.service';

/**
 * The studio's payment method visibility + ordering form. Embedded by both the
 * Payment Settings configuration page and the manage dialog opened from the
 * payment form.
 */
@Component({
  selector: 'app-payment-methods-form',
  templateUrl: './payment-methods-form.component.html',
  styleUrls: ['./payment-methods-form.component.css']
})
export class PaymentMethodsFormComponent implements OnInit {
  /** Hosts that render their own save button (the manage dialog puts it in
   *  mat-dialog-actions) set this to false. */
  @Input() showSaveButton = true;

  paymentMethodsForm: FormGroup;
  /** Starts true so the first paint is the loading state, not an empty list. */
  showProgressSpinner = true;
  successMessage = '';
  errorMessage = '';
  // Existing override entity id per term id, so saves update rows in place.
  private overrideEntityIds: { [termId: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private taxonomyService: TaxonomyService,
    private taxonomyOverridesService: TaxonomyOverridesService,
    private drupalRESTService: DrupalRESTService,
  ) {
    this.paymentMethodsForm = this.fb.group({ methods: this.fb.array([]) });
  }

  get methods(): FormArray {
    return this.paymentMethodsForm.get('methods') as FormArray;
  }

  get visibleCount(): number {
    return this.methods.controls.filter(control => control.get('visible')?.value).length;
  }

  ngOnInit(): void {
    this.loadPaymentMethods();
  }

  async loadPaymentMethods() {
    this.showProgressSpinner = true;
    this.errorMessage = '';
    try {
      await this.taxonomyService.initTaxonomyTerms();
      const overrides = await this.loadPaymentOverrides();
      this.initForm(overrides.overridden, overrides.hidden, overrides.weights);
    } catch (error) {
      LoggerService.error('Error loading payment methods:', error);
      this.errorMessage = 'Failed to load payment methods.';
    } finally {
      this.showProgressSpinner = false;
    }
  }

  private async loadPaymentOverrides(): Promise<{ overridden: Set<string>, hidden: Set<string>, weights: { [termId: string]: number } }> {
    const response: any = await this.drupalRESTService
      .httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_taxonomy_overrides_ref')
      .toPromise();

    let rows: any[] = [];
    if (Array.isArray(response)) {
      rows = response;
    } else if (response && typeof response === 'object' && !response.error) {
      // Backend returns a single object when the studio has exactly one override.
      rows = [response];
    }

    // Terms with any override row: the studio has made an explicit choice,
    // which beats the term's own hidden_by_default.
    const overridden = new Set<string>();
    const hidden = new Set<string>();
    const weights: { [termId: string]: number } = {};
    this.overrideEntityIds = {};
    for (const row of rows) {
      if (row?.field_vocabulary?.[0]?.value !== 'payment_type') { continue; }
      const termId = row?.field_term_id?.[0]?.value;
      if (!termId) { continue; }
      overridden.add(String(termId));
      const entityId = row?.id?.[0]?.value;
      if (entityId) {
        this.overrideEntityIds[String(termId)] = String(entityId);
      }
      const hide = row?.field_hide?.[0]?.value;
      if (hide === '1' || hide === true) {
        hidden.add(String(termId));
      }
      const weight = row?.field_weight?.[0]?.value;
      if (weight !== null && weight !== undefined && weight !== '') {
        weights[String(termId)] = Number(weight);
      }
    }
    return { overridden, hidden, weights };
  }

  private initForm(overriddenTermIds: Set<string>, hiddenTermIds: Set<string>, weights: { [termId: string]: number }) {
    // Studio's saved order first, alphabetical for anything without a weight.
    const ordered = [...this.taxonomyService.payment_type_all].sort((a, b) => {
      const weightA = weights[String(a.id)];
      const weightB = weights[String(b.id)];
      if (weightA != null && weightB != null) { return weightA - weightB; }
      if (weightA != null) { return -1; }
      if (weightB != null) { return 1; }
      return a.name.localeCompare(b.name);
    });

    const formArray = this.fb.array([]);
    for (const term of ordered) {
      formArray.push(this.fb.group({
        termId: [term.id],
        name: [term.name],
        // Studio's explicit choice if it has one; otherwise opt-in types start hidden.
        visible: [overriddenTermIds.has(String(term.id))
          ? !hiddenTermIds.has(String(term.id))
          : !term.hidden_by_default],
      }));
    }
    this.paymentMethodsForm = this.fb.group({ methods: formArray });
  }

  onDrop(event: CdkDragDrop<any>) {
    if (event.previousIndex === event.currentIndex) { return; }
    const control = this.methods.at(event.previousIndex);
    this.methods.removeAt(event.previousIndex);
    this.methods.insert(event.currentIndex, control);
  }

  onSubmit() {
    if (this.visibleCount === 0) {
      this.errorMessage = 'At least one payment method must remain visible.';
      return;
    }
    this.successMessage = '';
    this.errorMessage = '';
    this.showProgressSpinner = true;

    // One row per method: position becomes the weight, toggles become hide.
    const overrides = this.methods.controls.map((control, index) => {
      const termId = String(control.get('termId')?.value);
      const row: any = {
        field_vocabulary: [{ value: 'payment_type' }],
        field_term_id: [{ value: termId }],
        field_hide: [{ value: control.get('visible')?.value ? '0' : '1' }],
        field_published: [{ value: '1' }],
        field_weight: [{ value: String(index) }],
      };
      if (this.overrideEntityIds[termId]) {
        row.id = [{ value: this.overrideEntityIds[termId] }];
      }
      return row;
    });

    const postData = {
      config_field_name: 'field_taxonomy_overrides_ref',
      config_data: overrides,
      // Only replace payment_type rows: the calendar color overrides share
      // the same studio field and must survive this save.
      vocabulary_scope: ['payment_type'],
    };

    this.drupalRESTService.httpPOST('/api_rest/v1/saveStudioConfig', postData).subscribe(
      () => {
        this.successMessage = 'Payment methods updated.';
        this.showProgressSpinner = false;
        // Push the new visibility into the payment pickers app-wide.
        this.taxonomyOverridesService.refresh();
        this.loadPaymentMethods();
      },
      (error) => {
        LoggerService.error('Error saving payment method visibility:', error);
        this.errorMessage = 'Failed to update payment methods. Error: ' + (error?.message || 'Unknown error');
        this.showProgressSpinner = false;
      }
    );
  }
}
